import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  Form,
  Input,
  InputNumber,
  Radio,
  Checkbox,
  Modal,
  Popconfirm,
  Spin,
  notification,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import {
  getQuizByContentId,
  addQuizQuestion,
  updateQuizQuestion,
  deleteQuizQuestion
} from '../../../../../../Constants/Api/functions/function';
import './style.scss';

const { TextArea } = Input;

const QuizManager = ({ open, onClose, contentData, courseId, onQuizUpdated }) => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionType, setQuestionType] = useState('single');
  const [selectedCorrect, setSelectedCorrect] = useState([0]);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && contentData?.id) {
      loadQuiz();
    }
  }, [open, contentData]);

  const loadQuiz = async () => {
    if (!contentData?.id) return;
    setIsLoading(true);
    try {
      const res = await getQuizByContentId(contentData.id, null, 'admin');
      if (res && res.success) {
        setQuizData(res.quiz);
        setQuestions(res.questions || []);
      } else {
        setQuizData(null);
        setQuestions([]);
      }
    } catch (err) {
      console.error('Error loading quiz:', err);
    }
    setIsLoading(false);
  };

  const handleOpenAdd = () => {
    setEditingQuestion(null);
    setQuestionType('single');
    setSelectedCorrect([0]);
    form.resetFields();
    form.setFieldsValue({
      questionType: 'single',
      marks: 1,
      option1: '',
      option2: '',
      option3: '',
      option4: '',
      option5: '',
      explanation: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (q) => {
    setEditingQuestion(q);
    const qType = q.questionType || 'single';
    setQuestionType(qType);
    const correctArr = Array.isArray(q.correctAnswers) ? q.correctAnswers : [0];
    setSelectedCorrect(correctArr);

    form.setFieldsValue({
      questionText: q.questionText,
      questionType: qType,
      marks: q.marks,
      option1: q.option1,
      option2: q.option2,
      option3: q.option3,
      option4: q.option4,
      option5: q.option5,
      explanation: q.explanation || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (questionId) => {
    setIsLoading(true);
    try {
      const res = await deleteQuizQuestion(questionId);
      if (res && res.success) {
        api.success({
          message: 'Question Deleted',
          description: 'The question was successfully deleted.'
        });
        await loadQuiz();
        if (onQuizUpdated) onQuizUpdated();
      } else {
        api.error({ message: 'Error', description: res.message || 'Failed to delete question' });
      }
    } catch (err) {
      console.error('Delete question error:', err);
    }
    setIsLoading(false);
  };

  const handleSaveQuestion = async (values) => {
    if (!selectedCorrect || selectedCorrect.length === 0) {
      api.warning({
        message: 'Missing Correct Answer',
        description: 'Please select at least one correct answer for this question.'
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...values,
        correctAnswers: selectedCorrect,
        courseContentId: contentData.id,
        courseId: courseId,
        quizId: quizData?.id,
        orderNumber: editingQuestion ? editingQuestion.orderNumber : questions.length + 1
      };

      let res;
      if (editingQuestion) {
        payload.id = editingQuestion.id;
        res = await updateQuizQuestion(payload);
      } else {
        res = await addQuizQuestion(payload);
      }

      if (res && res.success) {
        api.success({
          message: editingQuestion ? 'Question Updated' : 'Question Added',
          description: 'Quiz question has been saved successfully.'
        });
        setIsModalOpen(false);
        await loadQuiz();
        if (onQuizUpdated) onQuizUpdated();
      } else {
        api.error({ message: 'Error', description: res.message || 'Failed to save question' });
      }
    } catch (err) {
      console.error('Save question error:', err);
      api.error({ message: 'Error', description: 'An error occurred while saving.' });
    }
    setIsLoading(false);
  };

  const totalMarks = questions.reduce((sum, q) => sum + (parseFloat(q.marks) || 0), 0);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <QuestionCircleOutlined />
          <span>Manage Quiz: {contentData?.title || 'Lesson'}</span>
        </div>
      }
      placement="right"
      width={720}
      onClose={onClose}
      open={open}
      className="quiz-manager-drawer"
      destroyOnClose
    >
      {contextHolder}
      <Spin spinning={isLoading} tip="Loading quiz...">
        <div className="quiz-summary-bar">
          <div className="summary-stats">
            <div className="stat-item">
              <span className="label">Total Questions</span>
              <span className="value">{questions.length}</span>
            </div>
            <div className="stat-item">
              <span className="label">Total Marks</span>
              <span className="value">{totalMarks.toFixed(1)}</span>
            </div>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleOpenAdd}
            style={{ borderRadius: 8 }}
          >
            Add Question
          </Button>
        </div>

        {questions.length === 0 ? (
          <div className="no-questions">
            <Empty
              description={
                <div>
                  <h3>No questions added yet</h3>
                  <p>Create a quiz for this course content with questions, 5 answers, and custom marks.</p>
                </div>
              }
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAdd}>
                Add First Question
              </Button>
            </Empty>
          </div>
        ) : (
          <div className="questions-list">
            {questions.map((q, idx) => {
              const qCorrect = Array.isArray(q.correctAnswers) ? q.correctAnswers : [];
              return (
                <div key={q.id || idx} className="question-card">
                  <div className="card-header">
                    <div className="q-meta">
                      <span className="q-badge">Q{idx + 1}</span>
                      <span className="q-type">
                        {q.questionType === 'multiple' ? 'Multiple Choice (Select all)' : 'Single Choice (1 Answer)'}
                      </span>
                      <span className="q-marks">{q.marks} {Number(q.marks) === 1 ? 'Mark' : 'Marks'}</span>
                    </div>
                    <div className="card-actions">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenEdit(q)}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete Question"
                        description="Are you sure you want to delete this question?"
                        onConfirm={() => handleDelete(q.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="text" danger icon={<DeleteOutlined />} size="small">
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>

                  <div className="question-text">{q.questionText}</div>

                  <div className="options-grid">
                    {[q.option1, q.option2, q.option3, q.option4, q.option5].map((optText, optIdx) => {
                      const isCorrect = qCorrect.includes(optIdx);
                      return (
                        <div key={optIdx} className={`option-row ${isCorrect ? 'correct' : ''}`}>
                          <span className="opt-badge">{String.fromCharCode(65 + optIdx)}</span>
                          <span className="opt-text">{optText}</span>
                          {isCorrect && (
                            <span className="correct-tag">
                              <CheckCircleOutlined style={{ marginRight: 4 }} /> Correct Answer
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {q.explanation && (
                    <div className="explanation-box">
                      <strong>Explanation: </strong> {q.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Spin>

      {/* Add / Edit Question Modal */}
      <Modal
        title={editingQuestion ? 'Edit Quiz Question' : 'Add New Quiz Question'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
        destroyOnClose
        className="question-form-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveQuestion}
          initialValues={{
            questionType: 'single',
            marks: 1
          }}
        >
          <Form.Item
            name="questionText"
            label="Question Text"
            rules={[{ required: true, message: 'Please enter the question' }]}
          >
            <TextArea rows={3} placeholder="Enter your question here..." />
          </Form.Item>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Form.Item
              name="questionType"
              label="Question Answer Type"
              rules={[{ required: true }]}
              style={{ flex: 1, minWidth: 240 }}
            >
              <Radio.Group
                onChange={(e) => {
                  const newType = e.target.value;
                  setQuestionType(newType);
                  if (newType === 'single') {
                    // Reduce to single element if multiple were selected
                    setSelectedCorrect(selectedCorrect.length > 0 ? [selectedCorrect[0]] : [0]);
                  }
                }}
              >
                <Radio value="single">Single Choice (1 Correct Answer)</Radio>
                <Radio value="multiple">Multiple Choice (Multiple Correct Answers)</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="marks"
              label="Marks / Points"
              rules={[{ required: true, message: 'Please set marks' }]}
              style={{ width: 140 }}
            >
              <InputNumber min={0.5} step={0.5} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div className="options-form-card">
            <div className="options-header">
              <span>5 Answers (All 5 required)</span>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                {questionType === 'single'
                  ? 'Select radio button for the single correct answer'
                  : 'Check all checkboxes that are correct answers'}
              </span>
            </div>

            {[0, 1, 2, 3, 4].map((idx) => {
              const letter = String.fromCharCode(65 + idx);
              const isChecked = selectedCorrect.includes(idx);

              return (
                <div key={idx} className="option-input-item">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {questionType === 'single' ? (
                      <Radio
                        checked={isChecked}
                        onChange={() => setSelectedCorrect([idx])}
                      >
                        <strong>Option {letter} (Correct)</strong>
                      </Radio>
                    ) : (
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCorrect([...selectedCorrect, idx]);
                          } else {
                            setSelectedCorrect(selectedCorrect.filter((i) => i !== idx));
                          }
                        }}
                      >
                        <strong>Option {letter} (Correct)</strong>
                      </Checkbox>
                    )}
                  </div>

                  <Form.Item
                    name={`option${idx + 1}`}
                    style={{ marginTop: 6, marginBottom: 12 }}
                    rules={[{ required: true, message: `Please enter Option ${letter}` }]}
                  >
                    <Input placeholder={`Enter Option ${letter} text...`} />
                  </Form.Item>
                </div>
              );
            })}
          </div>

          <Form.Item name="explanation" label="Explanation (Optional - shown to students after submitting)">
            <TextArea rows={2} placeholder="Explain why the answer is correct or provide guidance..." />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {editingQuestion ? 'Update Question' : 'Save Question'}
            </Button>
          </div>
        </Form>
      </Modal>
    </Drawer>
  );
};

export default QuizManager;
